import { useEffect, useState } from 'react'
import { Affix, Paper, Group, Text, Button, ActionIcon } from '@mantine/core'
import { useRouting, routingActions, UNDO_WINDOW_MS } from '../contexts/RoutingContext'

export interface UndoToastProps {
  /** Invoked when the user clicks Undo or presses Cmd/Ctrl+Z. The handler is
   * responsible for dispatching the inverse through the backend and removing
   * the entry from the undo buffer. */
  onUndo: () => void
}

/**
 * Floating toast pinned to the bottom-right. Shows the most recent undoable
 * routing change along with a countdown and an Undo button. The toast
 * disappears when the entry expires or is consumed. Keyboard shortcut
 * (Cmd/Ctrl+Z) is implemented in the App shell, not here.
 */
export function UndoToast({ onUndo }: UndoToastProps) {
  const { state, dispatch } = useRouting()
  const [now, setNow] = useState(() => Date.now())

  // Tick once per 200ms so the countdown updates smoothly.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 200)
    return () => clearInterval(id)
  }, [])

  const top = state.ui.undoBuffer[state.ui.undoBuffer.length - 1]
  if (!top) return null

  const remainingMs = Math.max(0, top.expiresAt - now)
  if (remainingMs === 0) return null

  const seconds = Math.ceil(remainingMs / 1000)
  const progressPct = Math.round((remainingMs / UNDO_WINDOW_MS) * 100)

  return (
    <Affix position={{ bottom: 20, right: 20 }}>
      <Paper
        shadow="md"
        radius="md"
        p="sm"
        withBorder
        data-testid="undo-toast"
        style={{ minWidth: 320, maxWidth: 400 }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} truncate>{top.description}</Text>
            <Text size="xs" c="dimmed">Undo available for {seconds}s</Text>
            {/* Minimal inline progress bar */}
            <div
              style={{
                marginTop: 4,
                height: 3,
                background: 'var(--mantine-color-gray-2)',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progressPct}%`,
                  height: '100%',
                  background: 'var(--mantine-color-blue-6)',
                  transition: 'width 200ms linear',
                }}
              />
            </div>
          </div>
          <Group gap="xs" wrap="nowrap">
            <Button size="compact-xs" onClick={onUndo} data-testid="undo-toast-button">Undo</Button>
            <ActionIcon
              size="sm"
              variant="subtle"
              aria-label="Dismiss undo"
              onClick={() => dispatch(routingActions.removeUndoEntry(top.id))}
            >
              ×
            </ActionIcon>
          </Group>
        </Group>
      </Paper>
    </Affix>
  )
}
